import React, { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { makeStyles } from '@mui/styles'

import ThemeInterface from '@/theme-interface'
import { formatCurrency } from '@/modules/_helpers/formatCurrency'

// FLAG:useDocuAi — invoice-review only: the invoice's OWN stated totals (net / VAT / gross as
// DocuAI read them off the document) are the truth the reviewer checks against, so they are shown
// as values rather than recomputed from the positions — and corrected in place, with the same
// one-click interaction as the line description.
//
// Deliberately a plain input, not the shared CurrencyInput: that component brings its own
// full-width wrapper plus hint/error slots, which broke out of the narrow, right-aligned totals
// column. The parsing it would have provided is a handful of lines (parseAmount below).
const useStyles = makeStyles((theme: ThemeInterface) => ({
  text: {
    cursor: 'text',
    borderRadius: 3,
    padding: '0 3px',
    margin: '0 -3px',
    '&:hover': {
      background: theme.backgroundColor,
      boxShadow: `inset 0 -1px 0 ${theme.borderColor}`,
    },
  },
  readOnly: {
    cursor: 'default',
    padding: 0,
    margin: 0,
    '&:hover': { background: 'none', boxShadow: 'none' },
  },
  input: {
    display: 'block',
    width: 96,
    maxWidth: '100%',
    boxSizing: 'border-box',
    font: 'inherit',
    color: theme.textColor,
    textAlign: 'right',
    background: theme.white,
    border: `1px solid ${theme.primary}`,
    borderRadius: 3,
    padding: '0 4px',
    margin: 0,
  },
}))

/**
 * Tolerant amount parser for hand-typed input: accepts "1234,56", "1.234,56", "1234.56" and
 * "1,234.56". The separator that appears LAST is the decimal one; a lone "." with exactly three
 * digits behind it (and no comma anywhere) is read as a German thousands separator — "1.234"
 * means 1234 to the people typing it, not 1.234.
 */
export const parseAmount = (raw: string): number | null => {
  const cleaned = (raw || '').replace(/[^\d,.-]/g, '').trim()
  if (!cleaned) { return null }
  const lastComma = cleaned.lastIndexOf(',')
  const lastDot = cleaned.lastIndexOf('.')
  let normalized = cleaned
  if (lastComma >= 0 || lastDot >= 0) {
    const decimalSep = lastComma > lastDot ? ',' : '.'
    const isGermanThousands = decimalSep === '.' && lastComma < 0 && /\.\d{3}$/.test(cleaned)
    if (isGermanThousands) {
      normalized = cleaned.split('.').join('')
    } else {
      const thousandSep = decimalSep === ',' ? '.' : ','
      normalized = cleaned.split(thousandSep).join('').replace(decimalSep, '.')
    }
  }
  const value = Number(normalized)
  return Number.isFinite(value) ? value : null
}

// What the field starts with: the raw number in German notation, so the reviewer edits digits
// rather than deleting a currency symbol first.
const toInputValue = (value?: number | null) => `${Number(value ?? 0).toFixed(2)}`.replace('.', ',')

interface InlineAmountEditProps {
  value?: number | null
  ariaLabel: string
  readOnly?: boolean
  title?: string
  dataQsId?: string
  // FLAG:useDocuAi — receipt currency code; without it formatCurrency would hard-code „€" below the
  // stated totals of a foreign-currency invoice.
  currencySymbol?: string
  onChange: (value: number) => void
}

export const InlineAmountEdit = (props: InlineAmountEditProps) => {
  const { value, ariaLabel, readOnly, title, dataQsId, currencySymbol, onChange } = props
  const classes = useStyles()
  const [draft, setDraft] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const cancelledRef = useRef(false)
  const isEditing = draft !== null

  useEffect(() => {
    const input = inputRef.current
    if (!input) { return }
    input.focus()
    input.select()
  }, [isEditing])

  const startEdit = () => {
    if (readOnly) { return }
    cancelledRef.current = false
    setDraft(toInputValue(value))
  }

  const commit = () => {
    const parsed = parseAmount(draft || '')
    setDraft(null)
    if (parsed === null) { return }
    if (parsed !== Number(value ?? 0)) { onChange(parsed) }
  }

  const handleBlur = () => {
    if (cancelledRef.current) { cancelledRef.current = false; return }
    commit()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      commit()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      cancelledRef.current = true
      setDraft(null)
    }
  }

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type='text'
        inputMode='decimal'
        className={classes.input}
        value={draft || ''}
        aria-label={ariaLabel}
        data-qs-id={dataQsId}
        onChange={event => setDraft(event.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
    )
  }

  return (
    <span
      className={clsx(classes.text, readOnly && classes.readOnly)}
      title={title}
      role={readOnly ? undefined : 'button'}
      tabIndex={readOnly ? undefined : 0}
      data-qs-id={dataQsId}
      onClick={startEdit}
      onKeyDown={(event) => {
        if (readOnly) { return }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          startEdit()
        }
      }}
    >
      {formatCurrency(Number(value ?? 0), currencySymbol ? { symbol: currencySymbol } : undefined)}
    </span>
  )
}

export default InlineAmountEdit
